"use client"

import { mediaFoldersToTreeData } from "@/(admin)/modules/media/utils/media-folder-to-tree"
import { MEDIA } from "@/constants/admin-paths"
import { useCheckPermission } from "@/hook/use-check-permission"
import { useDomainStore } from "@/stores/use-domain-store"
import { createPath } from "@/utils/create-path"
import { message, Modal } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useForm, useWatch } from "react-hook-form"
import { t } from "../../../../languages/i18n"
import { FileUpdate, FormValuesMedia, MediaData, MediaFolder } from "../../../../lib/config/types"
import {
  bulkDeleteMedia,
  deleteMedia,
  fetchMedia,
  moveMediaToFolder,
  updateMedia,
} from "../services/apiServices"
import { mediaFolderList } from "../services/media-folder-services"
import { resolveMediaDomainParam } from "../utils/media-domain"

export interface UseMediaListOptions {
  searchTerm?: string
  folderId?: number | null
  isView?: boolean
  isOpenModal?: boolean
  selectMode?: "single" | "multi"
  onSelectMedia?: (media: MediaData | MediaData[]) => void
  onBulkActionComplete?: () => void
  /** Page size from "Configure view" — drives the paged fetch. */
  viewPageSize?: number
  /** Default sort from "Configure view", e.g. "name:asc". */
  sort?: string
}

function usePagedMedia(
  searchTerm: string,
  folderId?: number | null,
  enabled = true,
  viewPageSize = 50,
  sort?: string,
) {
  // null = root view (backend filters folder IS NULL), like the CMS default —
  // not a hardcoded folder id.
  const resolvedFolderId = folderId ?? null
  const selectedDomain = useDomainStore(state => state.selectedDomain)
  const domain = resolveMediaDomainParam(selectedDomain)
  const [data, setData] = useState<MediaData[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: viewPageSize, total: 0 })

  const paginationRef = useRef(pagination)
  paginationRef.current = pagination

  // Guards against out-of-order responses: config hydration + folder/domain
  // changes can fire several fetches at once, and a slower stale one (e.g. the
  // pre-hydration pageSize) must not overwrite the latest result.
  const reqSeq = useRef(0)

  const fetchPage = useCallback(
    async (pg = paginationRef.current) => {
      const seq = ++reqSeq.current
      setLoading(true)
      try {
        const resp = await fetchMedia(pg.current, pg.pageSize, searchTerm, resolvedFolderId, domain, sort)
        if (seq !== reqSeq.current) return // a newer request superseded this one
        const list: MediaData[] = Array.isArray(resp?.data)
          ? resp.data
          : Array.isArray(resp)
            ? resp
            : []
        const totalFromApi =
          resp?.pagination?.total ??
          (Array.isArray(list) ? list.length : 0)

        setData(list)
        setPagination({ ...pg, total: totalFromApi })
      } catch (e) {
        console.error(e)
      } finally {
        if (seq === reqSeq.current) setLoading(false)
      }
    },
    [searchTerm, resolvedFolderId, domain, sort],
  )

  useEffect(() => {
    if (!enabled) return
    const first = { current: 1, pageSize: viewPageSize, total: 0 }
    setPagination(first)
    fetchPage(first)
    // Re-fetch page 1 whenever the configured page size or sort changes.
  }, [searchTerm, resolvedFolderId, enabled, domain, viewPageSize, sort])

  return { data, loading, pagination, fetchPage, setPagination }
}

export function useMediaList({
  searchTerm,
  folderId,
  isView = true,
  isOpenModal,
  selectMode = "single",
  onSelectMedia,
  onBulkActionComplete,
  viewPageSize = 20,
  sort,
}: UseMediaListOptions) {
  const { control, setValue } = useForm<FormValuesMedia>()
  const dataItemForm = useWatch({ control, name: "dataItem" })

  const router = useRouter()
  const searchParams = useSearchParams()
  // Prefer explicit searchTerm (modal mode keeps it in local state to avoid
  // polluting the host page URL); fall back to the URL param on the standalone page.
  const search = searchTerm !== undefined ? searchTerm : searchParams.get("name") || ""

  const { hasPermission: checkPermission } = useCheckPermission()
  const selectedDomain = useDomainStore(state => state.selectedDomain)
  const domain = resolveMediaDomainParam(selectedDomain)

  const canBulkDelete = checkPermission("media", "bulkDelete")
  const canBulkUpdate = checkPermission("media", "moveFilesToFolder")
  const [selectedMedia, setSelectedMedia] = useState<null | number>(null)
  const [selectedMediaMulti, setSelectedMediaMulti] = useState<MediaData[]>([])
  const [pendingMedia, setPendingMedia] = useState<MediaData | null>(null)
  const [bulkSelected, setBulkSelected] = useState<number[]>([])
  const [moveModalOpen, setMoveModalOpen] = useState(false)
  const [foldersForMove, setFoldersForMove] = useState<MediaFolder[]>([])
  const [foldersMoveLoading, setFoldersMoveLoading] = useState(false)
  const [moveTargetFolderId, setMoveTargetFolderId] = useState<number | null>(null)
  const [moving, setMoving] = useState(false)

  const [modal, contextHolder] = Modal.useModal()

  const resolvedFolderId = folderId ?? null

  // Ad-hoc sort chosen by clicking a column header — overrides the "Configure
  // view" default (`sort`) until cleared. Server-side pagination means client
  // sorting a single page is wrong; this feeds the actual API `sort` param.
  const [sortOverride, setSortOverride] = useState<string | undefined>(undefined)
  const effectiveSort = sortOverride ?? sort

  // Cả list lẫn grid đều dùng paged hook (phân trang thật). Grid trước đây dùng
  // infinite scroll + nút "Xem thêm" — bỏ vì khó điều hướng trong thư viện 60k+ file.
  const table = usePagedMedia(search, folderId, true, viewPageSize, effectiveSort)

  const dataItem: MediaData[] = table.data
  const loading: boolean = table.loading

  const refreshData = useCallback(async () => {
    await table.fetchPage({ ...table.pagination })
  }, [table])

  useEffect(() => {
    setValue("dataItem", dataItem)
  }, [dataItem, setValue])

  useEffect(() => {
    setBulkSelected([])
  }, [table.data])

  const allBulkSelected = dataItem.length > 0 && dataItem.every((m) => bulkSelected.includes(m.id))
  const someBulkSelected = bulkSelected.length > 0 && !allBulkSelected

  const moveTreeData = useMemo(
    () => mediaFoldersToTreeData(foldersForMove, resolvedFolderId),
    [foldersForMove, resolvedFolderId],
  )

  const toggleBulk = (id: number) => {
    setBulkSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const toggleBulkAll = (checked: boolean) => {
    setBulkSelected(checked ? dataItem.map((m) => m.id) : [])
  }

  const clearBulkSelection = () => setBulkSelected([])

  const handleBulkDelete = () => {
    if (bulkSelected.length === 0) {
      message.warning(t("No selected items"))
      return
    }
    if (!canBulkDelete) {
      message.warning(t("You don't have permission to delete this item!"))
      return
    }
    modal.confirm({
      title: t("Confirm Delete"),
      content: `${t("Bạn có chắc chắn muốn xóa")} ${bulkSelected.length} ${t("tệp đã chọn")}?`,
      okText: t("Delete"),
      cancelText: t("Cancel"),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await bulkDeleteMedia(bulkSelected, domain)
          message.success(t("Xóa thành công!"))
          setBulkSelected([])
          await refreshData()
          onBulkActionComplete?.()
        } catch {
          message.error(t("Xóa tệp thất bại."))
        }
      },
    })
  }

  const openBulkMoveModal = async () => {
    if (bulkSelected.length === 0 || !canBulkUpdate) return
    setMoveModalOpen(true)
    setMoveTargetFolderId(null)
    setFoldersMoveLoading(true)
    try {
      const resp = await mediaFolderList(null, undefined, domain)
      const list: MediaFolder[] = Array.isArray(resp?.data) ? resp.data : []
      setFoldersForMove(list)
    } catch (e) {
      console.error(e)
      message.error(t("Failed to load folders."))
      setMoveModalOpen(false)
    } finally {
      setFoldersMoveLoading(false)
    }
  }

  const closeBulkMoveModal = () => {
    setMoveModalOpen(false)
    setMoveTargetFolderId(null)
  }

  const handleConfirmBulkMove = async () => {
    if (bulkSelected.length === 0 || moveTargetFolderId == null || !canBulkUpdate) return
    setMoving(true)
    try {
      await moveMediaToFolder(bulkSelected, moveTargetFolderId, domain)
      message.success(t("Media moved successfully."))
      setBulkSelected([])
      setMoveModalOpen(false)
      setMoveTargetFolderId(null)
      await refreshData()
      onBulkActionComplete?.()
    } catch (e) {
      console.error(e)
      message.error(t("Di chuyển đa phương tiện thất bại."))
    } finally {
      setMoving(false)
    }
  }

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: t("Confirm Delete"),
      content: t("Are you sure you want to delete this media file?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          await deleteMedia(id, domain)
          message.success(t("Media file deleted successfully!"))
          await refreshData()
        } catch (error) {
          console.error("Handle Delete Error:", error)
          message.error(t("Failed to delete media file."))
        }
      },
    })
  }

  const handleFormSubmit = async (
    value: { title: string; alternativeText: string; caption: string; width?: number; height?: number },
    file?: File,
  ) => {
    try {
      const media = (dataItemForm || dataItem)[selectedMedia || 0]
      const formData = new FormData()
      const data: FileUpdate = {
        fileInfo: { name: value.title, alternativeText: value.alternativeText, caption: value.caption },
        file,
      }
      formData.append("fileInfo", JSON.stringify(data.fileInfo))
      if (data.file) formData.append("files", data.file as Blob)
      await updateMedia(media.id, formData, domain)
      await refreshData()
    } catch {
      message.error(t("Failed to update media information."))
    }
  }

  const handleSelectMultiMedia = (media: MediaData) => {
    const picked = selectedMediaMulti.some((m) => m.id === media.id)
    if (picked) {
      setSelectedMediaMulti((prev) => prev.filter((m) => m.id !== media.id))
    } else {
      setSelectedMediaMulti((prev) => [...prev, media])
    }
  }

  // "Chọn tất cả" cho picker multi: gom/bỏ toàn bộ ảnh của TRANG hiện tại vào tập
  // chèn (selectedMediaMulti) — khác với toggleBulkAll (tập xoá/di chuyển). Phân
  // trang thật nên chỉ tác động trang đang xem, giống modal picker cũ.
  const allMultiSelected =
    dataItem.length > 0 && dataItem.every((m) => selectedMediaMulti.some((s) => s.id === m.id))
  const someMultiSelected = selectedMediaMulti.length > 0 && !allMultiSelected

  const toggleSelectAllMulti = (checked: boolean) => {
    if (checked) {
      setSelectedMediaMulti((prev) => [
        ...prev,
        ...dataItem.filter((m) => !prev.some((s) => s.id === m.id)),
      ])
    } else {
      setSelectedMediaMulti((prev) => prev.filter((s) => !dataItem.some((m) => m.id === s.id)))
    }
  }

  const handleSelectMedia = (media: MediaData) => {
    if (onSelectMedia) {
      if (selectMode === "multi") {
        handleSelectMultiMedia(media)
      } else if (isOpenModal) {
        setPendingMedia(media)
      } else {
        onSelectMedia(media)
      }
    }
    setSelectedMedia(null)
  }

  const confirmPendingMedia = () => {
    if (pendingMedia && onSelectMedia) {
      onSelectMedia(pendingMedia)
      setPendingMedia(null)
    }
  }

  // Multi-select: trả cả mảng đã chọn về caller (nút "Select N"). Trước đây nút này
  // gọi nhầm handleSelectMedia(array) → rơi vào nhánh toggle nên không chèn được.
  const confirmMultiSelect = () => {
    if (onSelectMedia && selectedMediaMulti.length > 0) {
      ;(onSelectMedia as (m: MediaData[]) => void)(selectedMediaMulti)
      setSelectedMediaMulti([])
    }
  }

  const cancelPendingMedia = () => {
    setPendingMedia(null)
  }

  const navigateEditMedia = (direction: "prev" | "next") => {
    setSelectedMedia((prev) => (prev == null ? 0 : prev + (direction === "next" ? 1 : -1)))
  }

  const closeEditModal = () => setSelectedMedia(null)

  const handleTableChange = (
    pg: { current?: number; pageSize?: number },
    _filters?: unknown,
    sorter?: unknown,
    extra?: { action?: "paginate" | "sort" | "filter" },
  ) => {
    // Antd truyền `sorter` ở MỌI lần onChange (kể cả khi chỉ đổi trang), nên phải
    // dựa vào `extra.action` để phân biệt sort vs paginate — nếu chỉ xét `sorter`
    // thì click phân trang (không có sort) bị hiểu nhầm là "clear sort" và return sớm.
    if (extra?.action === "sort") {
      const s = sorter as { field?: string | (string | number)[]; order?: "ascend" | "descend" } | undefined
      if (s && !Array.isArray(s) && s.field && s.order) {
        const field = Array.isArray(s.field) ? s.field[s.field.length - 1] : s.field
        const order = s.order === "ascend" ? "asc" : "desc"
        setSortOverride(`${field}:${order}`)
      } else {
        // Bỏ sort (click lần 3) → về sort mặc định của "Configure view".
        setSortOverride(undefined)
      }
      window.scrollTo({ top: 0, behavior: "smooth" })
      return
    }

    const next = {
      current: pg.current || 1,
      pageSize: pg.pageSize || table.pagination.pageSize,
      total: table.pagination.total,
    }
    table.fetchPage(next)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleEditNavigate = (recordId: number) => {
    const queryString =
      searchParams.toString() || (typeof window !== "undefined" ? window.location.search : "")
    const params = new URLSearchParams(queryString)
    router.push(createPath(MEDIA.EDIT.PATH, params, { id: recordId }))
  }

  return {
    checkPermission,
    canBulkDelete,
    canBulkUpdate,

    dataItem,
    loading,
    table,
    refreshData,

    selectedMedia,
    setSelectedMedia,
    selectedMediaMulti,
    allMultiSelected,
    someMultiSelected,
    toggleSelectAllMulti,
    handleSelectMedia,
    handleSelectMultiMedia,
    pendingMedia,
    confirmPendingMedia,
    cancelPendingMedia,
    confirmMultiSelect,

    bulkSelected,
    allBulkSelected,
    someBulkSelected,
    toggleBulk,
    toggleBulkAll,
    clearBulkSelection,
    handleBulkDelete,

    moveModalOpen,
    moveTreeData,
    foldersMoveLoading,
    moveTargetFolderId,
    setMoveTargetFolderId,
    moving,
    openBulkMoveModal,
    closeBulkMoveModal,
    handleConfirmBulkMove,

    handleDelete,
    handleFormSubmit,
    handleTableChange,
    handleEditNavigate,
    navigateEditMedia,
    closeEditModal,

    contextHolder,

    isOpenModal,
    selectMode,
  }
}